import { useCallback, useEffect, useState } from "react";
import { api, money, formatApiError } from "@/lib/api";
import { Package, Boxes, Truck, Plus, Save, Trash2, ArrowDownToLine, ArrowUpFromLine, RefreshCw, AlertTriangle, Search, Edit3 } from "lucide-react";
import { toast } from "sonner";
import HelpTip from "@/components/HelpTip";
import { useAuth } from "@/contexts/AuthContext";

const TYPE_LABELS = {
    consumible: "Consumible",
    medicamento: "Medicamento",
    equipo_biomedico: "Equipo biomédico",
    refaccion: "Refacción vehicular",
    herramienta: "Herramienta",
};
const TYPE_COLORS = {
    consumible: "bg-emerald-100 text-emerald-800",
    medicamento: "bg-amber-100 text-amber-800",
    equipo_biomedico: "bg-sky-100 text-sky-800",
    refaccion: "bg-orange-100 text-orange-800",
    herramienta: "bg-violet-100 text-violet-800",
};

export default function Inventory() {
    const { user } = useAuth();
    const isAdmin = user?.role === "superadmin" || user?.role === "direccion";

    const [tab, setTab] = useState("catalog");
    const [items, setItems] = useState([]);
    const [warehouses, setWarehouses] = useState([]);
    const [units, setUnits] = useState([]);
    const [selectedWh, setSelectedWh] = useState(null);
    const [stock, setStock] = useState([]);
    const [movements, setMovements] = useState([]);

    const [filter, setFilter] = useState({ type: "", q: "" });
    const [showNewItem, setShowNewItem] = useState(false);
    const [newItem, setNewItem] = useState({ sku: "", name: "", type: "consumible", unit: "pieza", unit_cost: 0, public_price: 0, min_stock: 0, has_expiry: true });
    const [showNewWh, setShowNewWh] = useState(false);
    const [newWh, setNewWh] = useState({ name: "", type: "ambulancia", unit_id: "" });
    const [editingWh, setEditingWh] = useState(null); // {id, name, type, unit_id}

    const [movement, setMovement] = useState({ type: "entrada", item_id: "", qty: 1, warehouse_id: "", destination_warehouse_id: "", note: "", expiry_date: "" });

    const loadItems = useCallback(async () => {
        try {
            const params = {};
            if (filter.type) params.type = filter.type;
            if (filter.q) params.q = filter.q;
            const { data } = await api.get("/inventory/items", { params });
            setItems(data || []);
        } catch (e) { toast.error("No se pudo cargar el catálogo"); }
    }, [filter]);

    const loadWh = useCallback(async () => {
        try {
            const { data } = await api.get("/inventory/warehouses");
            setWarehouses(data || []);
            if (data?.length && !selectedWh) setSelectedWh(data[0]);
        } catch (e) { console.warn(e); }
        try {
            const { data } = await api.get("/units");
            setUnits(Array.isArray(data) ? data : (data?.items || []));
        } catch {}
    }, [selectedWh]);

    const loadStock = useCallback(async () => {
        if (!selectedWh) return;
        try {
            const { data } = await api.get(`/inventory/stock?warehouse_id=${selectedWh.id}`);
            setStock(data || []);
        } catch {}
        try {
            const { data } = await api.get(`/inventory/movements?warehouse_id=${selectedWh.id}&limit=50`);
            setMovements(data || []);
        } catch {}
    }, [selectedWh]);

    useEffect(() => { loadItems(); }, [loadItems]);
    useEffect(() => { loadWh(); }, [loadWh]);
    useEffect(() => { loadStock(); }, [loadStock]);

    // Auto-genera SKU del nombre + auto-calcula precio público con 35% utilidad sobre el costo
    const handleNewItemName = (name) => {
        const autoSku = name
            .toUpperCase()
            .normalize("NFD").replace(/[\u0300-\u036f]/g, "") // sin acentos
            .replace(/[^A-Z0-9 ]/g, "")
            .trim().split(/\s+/).slice(0, 3)
            .map(w => w.slice(0, 4)).join("-")
            .slice(0, 20);
        setNewItem(prev => ({ ...prev, name, sku: prev.sku || autoSku }));
    };
    const handleNewItemCost = (cost) => {
        const c = parseFloat(cost) || 0;
        setNewItem(prev => ({ ...prev, unit_cost: c, public_price: Math.round(c * 1.35 * 100) / 100 }));
    };

    const saveNewItem = async () => {
        if (!newItem.name) return toast.error("Nombre requerido (el SKU se genera automáticamente si lo dejas vacío)");
        try {
            await api.post("/inventory/items", newItem);
            toast.success("Item agregado al catálogo");
            setNewItem({ sku: "", name: "", type: "consumible", unit: "pieza", unit_cost: 0, public_price: 0, min_stock: 0, has_expiry: true });
            setShowNewItem(false);
            loadItems();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Error");
        }
    };

    const seedNom = async () => {
        if (!confirm("¿Sembrar el catálogo oficial NOM-005-SSA3-2018? Esto agregará insumos y equipo obligatorios que no existan aún (no duplica).")) return;
        try {
            const { data } = await api.post("/inventory/seed-nom");
            toast.success(`NOM sembrada: ${data.created} agregados · ${data.skipped} ya existían`);
            loadItems();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Error al sembrar NOM");
        }
    };

    const adjustStockManual = async (stockRow) => {
        const newQtyStr = prompt(
            `Ajuste manual de stock\n\n${stockRow.sku} · ${stockRow.name}\nStock actual: ${stockRow.qty} ${stockRow.unit}\n\nIngresa la cantidad nueva (exacta):`,
            String(stockRow.qty)
        );
        if (newQtyStr === null) return;
        const newQty = parseFloat(newQtyStr);
        if (isNaN(newQty) || newQty < 0) return toast.error("Cantidad inválida");
        const reason = prompt("Motivo del ajuste (queda en auditoría):", "Conteo físico");
        if (!reason) return;
        // Si caduca, también capturar fecha de caducidad
        let expiryDate = null;
        if (stockRow.has_expiry) {
            const exp = prompt(
                `Fecha de caducidad (YYYY-MM-DD) — opcional.\nActual: ${stockRow.next_expiry_date ? stockRow.next_expiry_date.slice(0,10) : "sin captura"}\n\nDeja vacío si no quieres cambiarla.`,
                stockRow.next_expiry_date ? stockRow.next_expiry_date.slice(0,10) : ""
            );
            if (exp && /^\d{4}-\d{2}-\d{2}$/.test(exp.trim())) {
                expiryDate = exp.trim();
            }
        }
        try {
            await api.post("/inventory/stock/adjust", {
                warehouse_id: stockRow.warehouse_id,
                item_id: stockRow.item_id,
                new_qty: newQty,
                reason,
                expiry_date: expiryDate,
            });
            toast.success(`Stock ajustado: ${stockRow.qty} → ${newQty}${expiryDate ? ` · vence ${expiryDate}` : ""}`);
            loadStock();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Error al ajustar");
        }
    };

    const saveNewWh = async () => {
        if (!newWh.name) return toast.error("Nombre requerido");
        try {
            await api.post("/inventory/warehouses", newWh);
            toast.success("Almacén creado");
            setNewWh({ name: "", type: "ambulancia", unit_id: "" });
            setShowNewWh(false);
            loadWh();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Error");
        }
    };

    const deleteItem = async (it) => {
        if (!window.confirm(`¿Eliminar item "${it.name}" del catálogo?\n\nSe desactiva (soft delete). El historial de movimientos se conserva.`)) return;
        try {
            await api.delete(`/inventory/items/${it.id}`);
            toast.success("Item eliminado del catálogo");
            loadItems();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Error");
        }
    };

    const saveEditWh = async () => {
        if (!editingWh?.name?.trim()) return toast.error("Nombre requerido");
        try {
            await api.patch(`/inventory/warehouses/${editingWh.id}`, {
                name: editingWh.name,
                type: editingWh.type,
                unit_id: editingWh.unit_id || null,
            });
            toast.success("Almacén actualizado");
            setEditingWh(null);
            loadWh();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Error");
        }
    };

    const deleteWh = async (w) => {
        if (w.type === "principal") return toast.error("No se puede eliminar el almacén principal");
        if (!window.confirm(`¿Eliminar almacén "${w.name}"?\n\nEl historial de movimientos se conserva pero el almacén dejará de aparecer en las listas.`)) return;
        try {
            await api.delete(`/inventory/warehouses/${w.id}`);
            toast.success("Almacén eliminado");
            if (selectedWh?.id === w.id) setSelectedWh(null);
            loadWh();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Error");
        }
    };

    const saveMovement = async () => {        if (!movement.item_id || movement.qty <= 0) return toast.error("Item y cantidad requeridos");
        try {
            await api.post("/inventory/movements", { ...movement, warehouse_id: selectedWh?.id, destination_warehouse_id: movement.type === "traspaso" ? movement.destination_warehouse_id : undefined });
            toast.success("Movimiento registrado");
            setMovement({ type: "entrada", item_id: "", qty: 1, warehouse_id: "", destination_warehouse_id: "", note: "", expiry_date: "" });
            loadStock();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Error");
        }
    };

    const TABS = [
        { id: "catalog", label: "Catálogo", icon: Package, count: items.length, help: "Lista maestra de consumibles, medicamentos y equipo biomédico que se cobran o se usan en servicios." },
        { id: "warehouses", label: "Almacenes", icon: Truck, count: warehouses.length, help: "Ubicaciones donde se guarda el inventario: almacén principal + botiquín de cada ambulancia." },
        { id: "stock", label: "Stock por almacén", icon: Boxes, count: stock.filter(s => s.is_low).length, help: "Cuánto hay físicamente en cada almacén. Los items en rojo están en mínimo de stock." },
        { id: "movements", label: "Movimientos", icon: RefreshCw, count: movements.length, help: "Historial de entradas, salidas y traspasos. Cada movimiento queda auditado." },
    ];

    return (
        <div className="space-y-4" data-testid="inventory-page">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h2 className="font-display font-black text-2xl tracking-tight flex items-center gap-2">
                        <Package className="w-6 h-6 text-red-600" /> Inventario
                        <HelpTip text="Sistema unificado: catálogo de productos + almacenes + stock + movimientos. Cuando registras consumo en el FRAP, el stock se descuenta automáticamente del botiquín de la ambulancia asignada." variant="?" />
                    </h2>
                    <div className="text-xs text-slate-500 font-mono">
                        {items.length} items · {warehouses.length} almacenes · {stock.filter(s => s.is_low).length} en mínimo de stock
                    </div>
                </div>
            </div>

            {/* Tabs */}
            <div className="border-b border-slate-200 flex flex-wrap gap-1">
                {TABS.map((t) => {
                    const Icon = t.icon;
                    const active = tab === t.id;
                    return (
                        <button
                            key={t.id}
                            onClick={() => setTab(t.id)}
                            className={`px-4 py-2 text-sm font-bold uppercase tracking-wider border-b-2 transition flex items-center gap-2 ${active ? "border-red-600 text-red-700" : "border-transparent text-slate-500 hover:text-slate-900"}`}
                            data-testid={`inv-tab-${t.id}`}
                            title={t.help}
                        >
                            <Icon className="w-3.5 h-3.5" /> {t.label}
                            {t.count > 0 && <span className={`text-[10px] font-mono ${active ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-600"} px-1.5 rounded-sm`}>{t.count}</span>}
                        </button>
                    );
                })}
            </div>

            {/* CATÁLOGO */}
            {tab === "catalog" && (
                <div className="space-y-3">
                    <div className="flex items-center gap-2 flex-wrap">
                        <div className="relative">
                            <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-slate-400" />
                            <input
                                value={filter.q}
                                onChange={(e) => setFilter({ ...filter, q: e.target.value })}
                                className="fm-input pl-8 w-64"
                                placeholder="Buscar por SKU o nombre"
                                data-testid="inv-search"
                            />
                        </div>
                        <select value={filter.type} onChange={(e) => setFilter({ ...filter, type: e.target.value })} className="fm-input w-48">
                            <option value="">Todos los tipos</option>
                            {Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                        </select>
                        <div className="flex-1"></div>
                        {isAdmin && (
                            <>
                                <button onClick={seedNom} className="fm-btn-outline" data-testid="inv-seed-nom-btn" title="Siembra el catálogo NOM-005-SSA3-2018 (insumos y equipo obligatorios para ambulancias en México).">
                                    <Plus className="w-3.5 h-3.5" /> Sembrar NOM
                                </button>
                                <button onClick={() => setShowNewItem(true)} className="fm-btn-primary" data-testid="inv-new-item-btn" title="Agrega un nuevo item al catálogo (medicamento, consumible o equipo).">
                                    <Plus className="w-3.5 h-3.5" /> Nuevo item
                                </button>
                            </>
                        )}
                    </div>

                    {showNewItem && (
                        <div className="fm-card p-4 border-l-4 border-l-red-500">
                            <h3 className="font-display font-bold text-sm mb-3">Nuevo item al catálogo</h3>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                                <input placeholder="SKU (opcional · se autogenera)" className="fm-input" value={newItem.sku} onChange={(e) => setNewItem({ ...newItem, sku: e.target.value.toUpperCase() })} data-testid="new-item-sku" />
                                <input placeholder="Nombre (al escribir se sugiere SKU)" className="fm-input col-span-2 md:col-span-3" value={newItem.name} onChange={(e) => handleNewItemName(e.target.value)} data-testid="new-item-name" autoComplete="off" spellCheck={false} />
                                <select className="fm-input" value={newItem.type} onChange={(e) => {
                                    const t = e.target.value;
                                    // Default has_expiry según el tipo (sin sobrescribir si el usuario ya lo cambió)
                                    const defaultExpires = t === "consumible" || t === "medicamento";
                                    setNewItem({ ...newItem, type: t, has_expiry: defaultExpires });
                                }}>
                                    {Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                                </select>
                                <input placeholder="Unidad (pieza, bolsa, etc.)" className="fm-input" value={newItem.unit} onChange={(e) => setNewItem({ ...newItem, unit: e.target.value })} />
                                <div>
                                    <label className="text-[10px] text-slate-500 font-mono">Costo</label>
                                    <input type="number" placeholder="0.00" className="fm-input" value={newItem.unit_cost} onChange={(e) => handleNewItemCost(e.target.value)} data-testid="new-item-cost" />
                                </div>
                                <div>
                                    <label className="text-[10px] text-slate-500 font-mono">Precio público (auto 35% utilidad)</label>
                                    <input type="number" placeholder="0.00" className="fm-input" value={newItem.public_price} onChange={(e) => setNewItem({ ...newItem, public_price: parseFloat(e.target.value) || 0 })} data-testid="new-item-price" />
                                </div>
                                <div>
                                    <label className="text-[10px] text-slate-500 font-mono">Stock mínimo</label>
                                    <input type="number" placeholder="0" className="fm-input" value={newItem.min_stock} onChange={(e) => setNewItem({ ...newItem, min_stock: parseInt(e.target.value) || 0 })} />
                                </div>
                                <div className="col-span-2 md:col-span-4 flex items-center gap-2 pt-1">
                                    <input
                                        type="checkbox"
                                        id="new-item-has-expiry"
                                        checked={!!newItem.has_expiry}
                                        onChange={(e) => setNewItem({ ...newItem, has_expiry: e.target.checked })}
                                        className="w-4 h-4 accent-red-600"
                                        data-testid="new-item-has-expiry"
                                    />
                                    <label htmlFor="new-item-has-expiry" className="text-xs text-slate-700 cursor-pointer select-none">
                                        Este artículo <strong>caduca</strong> (medicamentos y la mayoría de consumibles sí; equipos, herramientas y refacciones normalmente no).
                                        Cuando esté activo, al dar entrada podrás capturar la fecha de caducidad y aparecerá en el Centro de Alertas.
                                    </label>
                                </div>
                            </div>
                            <div className="flex gap-2 mt-3">
                                <button onClick={saveNewItem} className="fm-btn-primary"><Save className="w-3.5 h-3.5" /> Guardar</button>
                                <button onClick={() => setShowNewItem(false)} className="fm-btn-outline">Cancelar</button>
                            </div>
                        </div>
                    )}

                    <div className="fm-card overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="bg-slate-50 border-b border-slate-200">
                                    <th className="text-left py-2 px-3 font-bold text-slate-500 uppercase tracking-wider text-[10px]">SKU</th>
                                    <th className="text-left py-2 px-3 font-bold text-slate-500 uppercase tracking-wider text-[10px]">Nombre</th>
                                    <th className="text-left py-2 px-3 font-bold text-slate-500 uppercase tracking-wider text-[10px]">Tipo</th>
                                    <th className="text-left py-2 px-3 font-bold text-slate-500 uppercase tracking-wider text-[10px]">Unidad</th>
                                    <th className="text-right py-2 px-3 font-bold text-slate-500 uppercase tracking-wider text-[10px]">Costo</th>
                                    <th className="text-right py-2 px-3 font-bold text-slate-500 uppercase tracking-wider text-[10px]">Precio público</th>
                                    <th className="text-right py-2 px-3 font-bold text-slate-500 uppercase tracking-wider text-[10px]">Mín</th>
                                    {isAdmin && <th className="text-right py-2 px-3 font-bold text-slate-500 uppercase tracking-wider text-[10px]"></th>}
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((it) => (
                                    <tr key={it.id} className="border-b border-slate-100 hover:bg-slate-50/50">
                                        <td className="py-1.5 px-3 font-mono text-[11px] text-slate-600">{it.sku}</td>
                                        <td className="py-1.5 px-3">{it.name}</td>
                                        <td className="py-1.5 px-3"><span className={`text-[10px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded-sm ${TYPE_COLORS[it.type]}`}>{TYPE_LABELS[it.type]}</span></td>
                                        <td className="py-1.5 px-3 text-slate-500 text-xs">{it.unit}</td>
                                        <td className="py-1.5 px-3 text-right font-mono tabular-nums text-xs">{money(it.unit_cost)}</td>
                                        <td className="py-1.5 px-3 text-right font-mono tabular-nums font-bold">{money(it.public_price)}</td>
                                        <td className="py-1.5 px-3 text-right font-mono text-xs">{it.min_stock}</td>
                                        {isAdmin && (
                                            <td className="py-1.5 px-3 text-right">
                                                <button onClick={() => deleteItem(it)} className="text-red-400 hover:text-red-700" title="Eliminar item" data-testid={`delete-item-${it.id}`}>
                                                    <Trash2 className="w-3.5 h-3.5" />
                                                </button>
                                            </td>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* ALMACENES */}
            {tab === "warehouses" && (
                <div className="space-y-3">
                    <div className="flex items-center justify-end">
                        {isAdmin && (
                            <button onClick={() => setShowNewWh(true)} className="fm-btn-primary" data-testid="inv-new-wh-btn" title="Crea un nuevo almacén. Tipos: principal (base), ambulancia (botiquín de unidad).">
                                <Plus className="w-3.5 h-3.5" /> Nuevo almacén
                            </button>
                        )}
                    </div>
                    {showNewWh && (
                        <div className="fm-card p-4 border-l-4 border-l-red-500">
                            <h3 className="font-display font-bold text-sm mb-3">Nuevo almacén</h3>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                                <input placeholder="Nombre (ej. Botiquín AMB-103, Gavetas AMB-205)" className="fm-input" value={newWh.name} onChange={(e) => setNewWh({ ...newWh, name: e.target.value })} data-testid="new-wh-name" autoComplete="off" />
                                <select className="fm-input" value={newWh.type} onChange={(e) => setNewWh({ ...newWh, type: e.target.value })} data-testid="new-wh-type">
                                    <option value="ambulancia">Botiquín de ambulancia</option>
                                    <option value="gavetas_ambulancia">Gavetas de ambulancia</option>
                                    <option value="botiquin_portatil">Botiquín portátil (campo / eventos)</option>
                                    <option value="taller">Taller / Mantenimiento</option>
                                    <option value="equipo_biomedico">Equipo biomédico</option>
                                    <option value="refacciones">Refacciones y herramienta</option>
                                    <option value="oxigeno">Oxígeno y gases medicinales</option>
                                    <option value="base">Base operativa</option>
                                </select>
                                <select className="fm-input" value={newWh.unit_id} onChange={(e) => setNewWh({ ...newWh, unit_id: e.target.value })}>
                                    <option value="">Sin ambulancia</option>
                                    {units.map((u) => <option key={u.id} value={u.id}>{u.economic_number} · {u.plates}</option>)}
                                </select>
                            </div>
                            <div className="flex gap-2 mt-3">
                                <button onClick={saveNewWh} className="fm-btn-primary"><Save className="w-3.5 h-3.5" /> Crear</button>
                                <button onClick={() => setShowNewWh(false)} className="fm-btn-outline">Cancelar</button>
                            </div>
                        </div>
                    )}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {warehouses.map((w) => (
                            <div key={w.id} className="fm-card p-4 border-l-4 border-l-slate-300">
                                {editingWh?.id === w.id ? (
                                    <div className="space-y-2">
                                        <input
                                            value={editingWh.name}
                                            onChange={(e) => setEditingWh({ ...editingWh, name: e.target.value })}
                                            className="fm-input text-sm"
                                            placeholder="Nombre del almacén"
                                            data-testid={`edit-wh-name-${w.id}`}
                                        />
                                        <select
                                            value={editingWh.type}
                                            onChange={(e) => setEditingWh({ ...editingWh, type: e.target.value })}
                                            className="fm-input text-xs"
                                            disabled={w.type === "principal"}
                                        >
                                            <option value="ambulancia">Botiquín de ambulancia</option>
                                            <option value="botiquin_portatil">Botiquín portátil (campo / eventos)</option>
                                            <option value="taller">Taller / mantenimiento</option>
                                            <option value="equipo_biomedico">Almacén equipo biomédico</option>
                                            <option value="refacciones">Almacén refacciones y herramienta</option>
                                            <option value="principal">Almacén principal</option>
                                        </select>
                                        <select
                                            value={editingWh.unit_id || ""}
                                            onChange={(e) => setEditingWh({ ...editingWh, unit_id: e.target.value })}
                                            className="fm-input text-xs"
                                        >
                                            <option value="">Sin ambulancia</option>
                                            {units.map((u) => <option key={u.id} value={u.id}>{u.economic_number} · {u.plates}</option>)}
                                        </select>
                                        <div className="flex gap-1.5">
                                            <button onClick={saveEditWh} className="fm-btn-primary text-xs flex-1" data-testid={`save-edit-wh-${w.id}`}>
                                                <Save className="w-3 h-3" /> Guardar
                                            </button>
                                            <button onClick={() => setEditingWh(null)} className="fm-btn-outline text-xs">Cancelar</button>
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        <div className="flex items-start justify-between mb-2">
                                            <div className="flex-1 min-w-0">
                                                <div
                                                    className={`font-display font-bold text-base truncate ${isAdmin ? "cursor-pointer hover:text-indigo-600" : ""}`}
                                                    onDoubleClick={() => isAdmin && setEditingWh({ id: w.id, name: w.name, type: w.type, unit_id: w.unit_id || "" })}
                                                    title={isAdmin ? "Doble-click para renombrar" : ""}
                                                    data-testid={`wh-title-${w.id}`}
                                                >
                                                    {w.name}
                                                </div>
                                                <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">{w.type}</div>
                                            </div>
                                            {isAdmin && (
                                                <div className="flex gap-1 ml-2">
                                                    <button
                                                        onClick={() => setEditingWh({ id: w.id, name: w.name, type: w.type, unit_id: w.unit_id || "" })}
                                                        className="text-slate-400 hover:text-indigo-600 p-1"
                                                        title="Renombrar / editar almacén"
                                                        data-testid={`edit-wh-btn-${w.id}`}
                                                    >
                                                        <Edit3 className="w-3.5 h-3.5" />
                                                    </button>
                                                    {w.type !== "principal" && (
                                                        <button
                                                            onClick={() => deleteWh(w)}
                                                            className="text-red-400 hover:text-red-700 p-1"
                                                            title="Eliminar almacén"
                                                            data-testid={`delete-wh-btn-${w.id}`}
                                                        >
                                                            <Trash2 className="w-3.5 h-3.5" />
                                                        </button>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                        {w.unit_label && (
                                            <div className="text-xs text-slate-600 mb-1 font-mono">🚑 {w.unit_label}</div>
                                        )}
                                        <button onClick={() => { setSelectedWh(w); setTab("stock"); }} className="fm-btn-outline w-full justify-center mt-2 text-xs">
                                            Ver stock
                                        </button>
                                    </>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* STOCK */}
            {tab === "stock" && (
                <div className="space-y-3">
                    <div className="flex items-center gap-3 flex-wrap">
                        <select value={selectedWh?.id || ""} onChange={(e) => setSelectedWh(warehouses.find((w) => w.id === e.target.value))} className="fm-input w-64" data-testid="stock-wh-select">
                            {warehouses.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
                        </select>
                        <div className="flex-1"></div>
                        <div className="text-xs text-slate-500 font-mono">{stock.length} items en este almacén</div>
                    </div>

                    {/* Quick movement */}
                    {isAdmin && selectedWh && (
                        <div className="fm-card p-4 border-l-4 border-l-emerald-500">
                            <h3 className="font-display font-bold text-sm mb-2">Registrar movimiento</h3>
                            <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
                                <select value={movement.type} onChange={(e) => setMovement({ ...movement, type: e.target.value })} className="fm-input" data-testid="movement-type">
                                    <option value="entrada">Entrada (compra)</option>
                                    <option value="salida">Salida (consumo)</option>
                                    <option value="traspaso">Traspaso</option>
                                </select>
                                <select value={movement.item_id} onChange={(e) => setMovement({ ...movement, item_id: e.target.value })} className="fm-input md:col-span-2" data-testid="movement-item">
                                    <option value="">Selecciona item...</option>
                                    {items.map((it) => <option key={it.id} value={it.id}>{it.sku} · {it.name}</option>)}
                                </select>
                                <input type="number" min="0" step="1" value={movement.qty} onChange={(e) => setMovement({ ...movement, qty: parseFloat(e.target.value) || 0 })} placeholder="Cantidad" className="fm-input text-right" data-testid="movement-qty" />
                                {movement.type === "traspaso" && (
                                    <select value={movement.destination_warehouse_id} onChange={(e) => setMovement({ ...movement, destination_warehouse_id: e.target.value })} className="fm-input">
                                        <option value="">Destino...</option>
                                        {warehouses.filter((w) => w.id !== selectedWh?.id).map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
                                    </select>
                                )}
                                <button onClick={saveMovement} className="fm-btn-primary" data-testid="movement-save">
                                    {movement.type === "entrada" ? <ArrowDownToLine className="w-3.5 h-3.5" /> : movement.type === "salida" ? <ArrowUpFromLine className="w-3.5 h-3.5" /> : <RefreshCw className="w-3.5 h-3.5" />}
                                    {movement.type === "entrada" ? "Entrar" : movement.type === "salida" ? "Salir" : "Traspasar"}
                                </button>
                            </div>
                            {/* Caducidad solo aplica a entradas/traspasos de items que caducan */}
                            {(movement.type === "entrada" || movement.type === "traspaso") && (() => {
                                const item = items.find((it) => it.id === movement.item_id);
                                if (!item || !item.has_expiry) return null;
                                return (
                                    <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-2 items-end">
                                        <div>
                                            <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Fecha de caducidad del lote</label>
                                            <input
                                                type="date"
                                                value={movement.expiry_date}
                                                onChange={(e) => setMovement({ ...movement, expiry_date: e.target.value })}
                                                className="fm-input"
                                                data-testid="movement-expiry-date"
                                                title="Captura la fecha de caducidad impresa en el empaque. Se usará para alertas y consumo FIFO."
                                            />
                                        </div>
                                        <div className="text-[11px] text-slate-500 md:col-span-2">
                                            Este artículo está marcado como caducable. La fecha que ingreses se usará para alertas. Si el lote ya está cargado y no la conoces, déjala vacía y captúrala después con "Ajustar".
                                        </div>
                                    </div>
                                );
                            })()}
                        </div>
                    )}

                    <div className="fm-card overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="bg-slate-50 border-b border-slate-200">
                                    <th className="text-left py-2 px-3 font-bold text-slate-500 uppercase tracking-wider text-[10px]">SKU</th>
                                    <th className="text-left py-2 px-3 font-bold text-slate-500 uppercase tracking-wider text-[10px]">Item</th>
                                    <th className="text-right py-2 px-3 font-bold text-slate-500 uppercase tracking-wider text-[10px]">Stock</th>
                                    <th className="text-right py-2 px-3 font-bold text-slate-500 uppercase tracking-wider text-[10px]">Mínimo</th>
                                    <th className="text-right py-2 px-3 font-bold text-slate-500 uppercase tracking-wider text-[10px]">Valor</th>
                                    <th className="text-left py-2 px-3 font-bold text-slate-500 uppercase tracking-wider text-[10px]">Caduca</th>
                                    {isAdmin && <th className="text-center py-2 px-3 font-bold text-slate-500 uppercase tracking-wider text-[10px]">Ajustar</th>}
                                </tr>
                            </thead>
                            <tbody>
                                {stock.map((s) => {
                                    const expBadge = (() => {
                                        if (!s.has_expiry) return <span className="text-[10px] text-slate-300">N/A</span>;
                                        if (!s.next_expiry_date) return <span className="text-[10px] text-slate-400">— sin captura —</span>;
                                        const cls = s.expiry_status === "vencido" ? "bg-red-700 text-white"
                                            : s.expiry_status === "critico" ? "bg-red-500 text-white"
                                                : s.expiry_status === "alerta" ? "bg-amber-500 text-white"
                                                    : "bg-emerald-100 text-emerald-800";
                                        const txt = s.expiry_status === "vencido" ? `VENCIDO ${Math.abs(s.days_to_expiry)}d`
                                            : `${s.next_expiry_date.slice(0, 10)} · ${s.days_to_expiry}d`;
                                        return <span className={`inline-block px-2 py-0.5 rounded-sm text-[10px] font-bold ${cls}`}>{txt}</span>;
                                    })();
                                    return (
                                    <tr key={`${s.warehouse_id}-${s.item_id}`} className={`border-b border-slate-100 ${s.expiry_status === "vencido" ? "bg-red-100/60" : s.is_low ? "bg-red-50/40" : "hover:bg-slate-50/50"}`}>
                                        <td className="py-1.5 px-3 font-mono text-[11px] text-slate-600">{s.sku}</td>
                                        <td className="py-1.5 px-3">{s.name}</td>
                                        <td className={`py-1.5 px-3 text-right font-mono font-bold ${s.is_low ? "text-red-700" : ""}`}>
                                            {s.qty.toFixed(s.qty % 1 ? 1 : 0)} {s.unit}
                                            {s.is_low && <AlertTriangle className="w-3 h-3 inline ml-1 text-red-600" />}
                                        </td>
                                        <td className="py-1.5 px-3 text-right font-mono text-xs text-slate-500">{s.min_stock}</td>
                                        <td className="py-1.5 px-3 text-right font-mono tabular-nums text-xs">{money(s.qty * s.unit_cost)}</td>
                                        <td className="py-1.5 px-3">{expBadge}</td>
                                        {isAdmin && (
                                            <td className="py-1.5 px-3 text-center">
                                                <button
                                                    onClick={() => adjustStockManual(s)}
                                                    className="text-[10px] uppercase font-bold text-amber-700 hover:text-amber-900"
                                                    data-testid={`adjust-stock-${s.item_id}`}
                                                    title="Ajustar stock exacto (conteo físico, mermas, etc.). Solo Superadmin/Dirección. Queda auditado."
                                                >
                                                    Ajustar
                                                </button>
                                            </td>
                                        )}
                                    </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* MOVIMIENTOS */}
            {tab === "movements" && (
                <div className="fm-card overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="bg-slate-50 border-b border-slate-200">
                                <th className="text-left py-2 px-3 font-bold text-slate-500 uppercase tracking-wider text-[10px]">Fecha</th>
                                <th className="text-left py-2 px-3 font-bold text-slate-500 uppercase tracking-wider text-[10px]">Tipo</th>
                                <th className="text-left py-2 px-3 font-bold text-slate-500 uppercase tracking-wider text-[10px]">Item</th>
                                <th className="text-right py-2 px-3 font-bold text-slate-500 uppercase tracking-wider text-[10px]">Cantidad</th>
                                <th className="text-right py-2 px-3 font-bold text-slate-500 uppercase tracking-wider text-[10px]">Costo</th>
                                <th className="text-left py-2 px-3 font-bold text-slate-500 uppercase tracking-wider text-[10px]">Por</th>
                                <th className="text-left py-2 px-3 font-bold text-slate-500 uppercase tracking-wider text-[10px]">Nota / Referencia</th>
                            </tr>
                        </thead>
                        <tbody>
                            {movements.map((m) => (
                                <tr key={m.id} className="border-b border-slate-100">
                                    <td className="py-1.5 px-3 text-xs font-mono">{new Date(m.at).toLocaleString("es-MX")}</td>
                                    <td className="py-1.5 px-3"><span className={`text-[10px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded-sm ${m.type === "entrada" ? "bg-emerald-100 text-emerald-800" : m.type === "salida" ? "bg-red-100 text-red-700" : "bg-sky-100 text-sky-800"}`}>{m.type}</span></td>
                                    <td className="py-1.5 px-3"><span className="font-mono text-[10px] text-slate-500">{m.sku}</span> {m.item_name}</td>
                                    <td className="py-1.5 px-3 text-right font-mono tabular-nums font-bold">{m.qty}</td>
                                    <td className="py-1.5 px-3 text-right font-mono tabular-nums">{money(m.total_cost || 0)}</td>
                                    <td className="py-1.5 px-3 text-xs">{m.by_name || "—"}</td>
                                    <td className="py-1.5 px-3 text-xs text-slate-500">{m.reference_type === "frap" ? `🩺 FRAP servicio` : m.note || "—"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
